import { MAX_SEARCH_KEYWORDS } from './multiKeywordSearch';

// AST node types
export type SearchNode =
    | { type: 'term'; value: string; negated: boolean }
    | { type: 'phrase'; value: string; negated: boolean }
    | { type: 'and'; left: SearchNode; right: SearchNode }
    | { type: 'or'; left: SearchNode; right: SearchNode };

// Token types
type Token =
    | { type: 'TERM'; value: string }
    | { type: 'PHRASE'; value: string }
    | { type: 'AND' }
    | { type: 'OR' }
    | { type: 'NOT' };

const OPERATOR_REGEX = /^(AND|OR|NOT)$/i;

/**
 * Tokenize search input into structured tokens.
 * Handles quoted phrases, boolean operators (case-insensitive), and plain terms.
 */
export const tokenize = (input: string): Token[] => {
    const tokens: Token[] = [];
    let i = 0;
    const str = input.trim();

    while (i < str.length) {
        // Skip whitespace
        if (/\s/.test(str[i])) {
            i++;
            continue;
        }

        // Quoted phrase
        if (str[i] === '"') {
            const closing = str.indexOf('"', i + 1);
            if (closing !== -1) {
                const phrase = str.slice(i + 1, closing).trim();
                if (phrase.length > 0) {
                    tokens.push({ type: 'PHRASE', value: phrase });
                }
                i = closing + 1;
                continue;
            }
            // Unbalanced quote — treat rest as a plain term
            const rest = str.slice(i + 1).trim();
            if (rest.length > 0) {
                tokens.push({ type: 'TERM', value: rest });
            }
            break;
        }

        // Read a word
        let end = i;
        while (end < str.length && !/\s/.test(str[end]) && str[end] !== '"') {
            end++;
        }
        const word = str.slice(i, end);
        i = end;

        if (OPERATOR_REGEX.test(word)) {
            const op = word.toUpperCase();
            if (op === 'AND') tokens.push({ type: 'AND' });
            else if (op === 'OR') tokens.push({ type: 'OR' });
            else tokens.push({ type: 'NOT' });
        } else {
            tokens.push({ type: 'TERM', value: word });
        }
    }

    return tokens;
};

/**
 * Count leaf nodes (terms + phrases) in an AST.
 */
export const countLeafNodes = (node: SearchNode): number => {
    if (node.type === 'term' || node.type === 'phrase') return 1;
    return countLeafNodes(node.left) + countLeafNodes(node.right);
};

/**
 * Parse tokens into an AST with precedence: NOT > AND (implicit/explicit) > OR.
 *
 * Grammar:
 *   expr     → andExpr (OR andExpr)*
 *   andExpr  → unary (AND? unary)*
 *   unary    → NOT? primary
 *   primary  → TERM | PHRASE
 */
const parse = (tokens: Token[]): SearchNode => {
    let pos = 0;

    const peek = (): Token | undefined => tokens[pos];
    const advance = (): Token => tokens[pos++];

    const parsePrimary = (): SearchNode | null => {
        const token = peek();
        if (!token) return null;

        if (token.type === 'TERM') {
            advance();
            return { type: 'term', value: token.value, negated: false };
        }
        if (token.type === 'PHRASE') {
            advance();
            return { type: 'phrase', value: token.value, negated: false };
        }

        return null;
    };

    const parseUnary = (): SearchNode | null => {
        // Count leading NOTs; even count cancels, odd count negates. Recursing instead of
        // just calling parsePrimary here means `NOT NOT x` doesn't silently drop x.
        let negate = false;
        while (peek()?.type === 'NOT') {
            advance();
            negate = !negate;
        }
        const operand = parsePrimary();
        if (!operand) return null; // Trailing NOT(s) with no operand
        if (negate && (operand.type === 'term' || operand.type === 'phrase')) {
            operand.negated = !operand.negated;
        }
        return operand;
    };

    // Skip stray binary operators (AND/OR) at positions where a unary expression is expected.
    // Handles inputs like `foo AND AND bar` or `foo AND OR bar` by discarding the extra operator
    // instead of silently dropping the following term.
    const skipStrayBinaryOps = (): void => {
        while (peek()?.type === 'AND' || peek()?.type === 'OR') {
            advance();
        }
    };

    const parseAndExpr = (): SearchNode | null => {
        let left = parseUnary();
        if (!left) return null;

        while (pos < tokens.length) {
            const token = peek();
            // Explicit AND
            if (token?.type === 'AND') {
                advance();
                skipStrayBinaryOps();
                const right = parseUnary();
                if (!right) break;
                left = { type: 'and', left, right };
                continue;
            }
            // Implicit AND: next token is a TERM, PHRASE, or NOT (not OR)
            if (token?.type === 'TERM' || token?.type === 'PHRASE' || token?.type === 'NOT') {
                const right = parseUnary();
                if (!right) break;
                left = { type: 'and', left, right };
                continue;
            }
            break;
        }

        return left;
    };

    const parseExpr = (): SearchNode | null => {
        let left = parseAndExpr();
        if (!left) return null;

        while (pos < tokens.length) {
            const token = peek();
            if (token?.type === 'OR') {
                advance();
                skipStrayBinaryOps();
                const right = parseAndExpr();
                if (!right) break;
                left = { type: 'or', left, right };
                continue;
            }
            break;
        }

        return left;
    };

    return parseExpr()!;
};

/**
 * Highlight span: contiguous slice of the original input tagged with a token type.
 * Unlike `tokenize`, spans cover 100% of the input (whitespace included) and preserve
 * character offsets so the rendering layer can paint each segment in place.
 */
export type HighlightSpan = {
    type: 'TERM' | 'PHRASE' | 'OPERATOR' | 'WILDCARD' | 'WHITESPACE';
    start: number;
    end: number;
};

/**
 * Walk the input identically to `tokenize` but emit position-aware spans for the
 * syntax-highlighting overlay. Quoted phrases include their surrounding quote chars in a
 * single PHRASE span (operators inside a phrase are not re-highlighted). An unbalanced
 * trailing `"` is rendered as PHRASE through end-of-input so the user gets immediate visual
 * feedback while typing. Wildcard chars (`*`, `?`) inside a term are split out as their own
 * WILDCARD spans so they can be coloured independently.
 */
export const tokenizeWithSpans = (input: string): HighlightSpan[] => {
    const spans: HighlightSpan[] = [];
    let i = 0;

    while (i < input.length) {
        if (/\s/.test(input[i])) {
            const start = i;
            while (i < input.length && /\s/.test(input[i])) i++;
            spans.push({ type: 'WHITESPACE', start, end: i });
            continue;
        }

        if (input[i] === '"') {
            const start = i;
            const closing = input.indexOf('"', i + 1);
            i = closing !== -1 ? closing + 1 : input.length;
            spans.push({ type: 'PHRASE', start, end: i });
            continue;
        }

        const wordStart = i;
        while (i < input.length && !/\s/.test(input[i]) && input[i] !== '"') i++;
        const word = input.slice(wordStart, i);

        if (OPERATOR_REGEX.test(word)) {
            spans.push({ type: 'OPERATOR', start: wordStart, end: i });
            continue;
        }

        // Plain term — split out wildcard chars so they can be colored independently.
        let segStart = wordStart;
        for (let j = wordStart; j < i; j++) {
            if (input[j] === '*' || input[j] === '?') {
                if (j > segStart) spans.push({ type: 'TERM', start: segStart, end: j });
                spans.push({ type: 'WILDCARD', start: j, end: j + 1 });
                segStart = j + 1;
            }
        }
        if (segStart < i) spans.push({ type: 'TERM', start: segStart, end: i });
    }

    return spans;
};

/**
 * Check if the input contains any search syntax operators.
 */
export const hasSearchOperators = (input: string): boolean => {
    // Check for quoted phrases
    if (/"[^"]*"/.test(input)) return true;
    // Check for boolean operators as whole words
    if (/\b(AND|OR|NOT)\b/i.test(input)) return true;
    // Check for wildcards
    if (/[*?]/.test(input)) return true;
    return false;
};

export interface ParsedSearchQuery {
    ast: SearchNode;
    /** True when the AST had more than MAX_SEARCH_KEYWORDS leaves and was truncated. */
    truncated: boolean;
}

/**
 * Parse a search query string into an AST plus a truncation flag. Throws if parsing fails
 * (caller should catch and fall back to legacy behavior). The truncation flag is separate from
 * the AST so callers can surface a warning without double-parsing to check.
 */
export const parseSearchQuery = (input: string): ParsedSearchQuery => {
    const tokens = tokenize(input);
    if (tokens.length === 0) {
        throw new Error('Empty search query');
    }

    // Filter out leading/trailing operators and wildcard-only terms (e.g. `*`, `?`, `*?`) —
    // the latter would otherwise produce match-all RSQL like `=lk="%"`.
    const cleaned = cleanTokens(tokens);
    if (cleaned.length === 0) {
        throw new Error('No valid search terms');
    }

    const ast = parse(cleaned);
    if (!ast) {
        throw new Error('Failed to parse search query');
    }

    const truncated = countLeafNodes(ast) > MAX_SEARCH_KEYWORDS;
    return {
        ast: truncated ? truncateAst(ast, MAX_SEARCH_KEYWORDS) : ast,
        truncated,
    };
};

/**
 * A term token that, after stripping `*` and `?`, has no meaningful content. Such tokens
 * would otherwise produce match-all RSQL and are dropped before parsing.
 */
const isWildcardOnlyTerm = (token: Token): boolean => token.type === 'TERM' && /^[\s*?]+$/.test(token.value);

/**
 * Clean tokens: remove wildcard-only terms and leading/trailing operators without operands.
 */
const cleanTokens = (tokens: Token[]): Token[] => {
    const result = tokens.filter((t) => !isWildcardOnlyTerm(t));

    // Remove leading binary operators (AND, OR)
    while (result.length > 0 && (result[0].type === 'AND' || result[0].type === 'OR')) {
        result.shift();
    }

    // Remove trailing operators
    while (
        result.length > 0 &&
        (result[result.length - 1].type === 'AND' ||
            result[result.length - 1].type === 'OR' ||
            result[result.length - 1].type === 'NOT')
    ) {
        result.pop();
    }

    return result;
};

/**
 * Truncate AST to max leaf nodes by pruning rightmost branches while preserving
 * the tree structure (AND/OR/NOT relationships are maintained).
 */
const truncateAst = (node: SearchNode, max: number): SearchNode => {
    const counter = { remaining: max };
    const result = pruneNode(node, counter);
    return result ?? { type: 'term', value: '', negated: false };
};

const pruneNode = (node: SearchNode, counter: { remaining: number }): SearchNode | null => {
    if (node.type === 'term' || node.type === 'phrase') {
        if (counter.remaining <= 0) return null;
        counter.remaining--;
        return node;
    }

    const left = pruneNode(node.left, counter);
    if (!left) return null;

    const right = pruneNode(node.right, counter);
    if (!right) return left;

    return { ...node, left, right };
};
